export type DownloadStrategy = 'browser_download' | 'native_download' | 'native_share';
export type DownloadEvent = 'download_started' | 'download_native_started' | 'download_shared' | 'download_completed_or_handoff'
  | 'download_cancelled' | 'download_failed' | 'download_fallback';
export type DownloadTimingStage = 'blob_ready' | 'ticket_started' | 'ticket_ready' | 'native_invoked' | 'handoff_started';
export interface DownloadResult { strategy: DownloadStrategy }

interface DownloadOptions {
  platform?: string;
  userAgent?: string;
  operationKey?: string;
  nativeUrl?: () => Promise<{ url: string; fileName?: string }>;
  nativeDownload?: (value: { url: string; file_name: string }) => Promise<boolean>;
  onEvent?: (event: DownloadEvent, metadata: Record<string, string>) => void;
  onTiming?: (stage: DownloadTimingStage, elapsedMs: number) => void;
}

const inFlightDownloads = new Map<string, Promise<DownloadResult>>();

function platformKind(platform: string, userAgent: string): 'android' | 'ios' | 'desktop' {
  const normalized = platform.toLowerCase();
  if (normalized === 'android' || /Android/i.test(userAgent)) return 'android';
  if (normalized === 'ios' || /iPhone|iPad/i.test(userAgent)) return 'ios';
  return 'desktop';
}

function browserDownload(file: File): void {
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url; link.download = file.name; link.rel = 'noopener';
  document.body.append(link); link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function imageFileType(blob: Blob, name: string): string {
  const declared = blob.type.trim().toLowerCase();
  if (declared.startsWith('image/')) return declared === 'image/jpg' ? 'image/jpeg' : declared;
  const extension = name.split('.').at(-1)?.toLowerCase();
  if (extension === 'png') return 'image/png';
  if (extension === 'webp') return 'image/webp';
  return 'image/jpeg';
}

async function shareFile(file: File): Promise<void> {
  const shareData: ShareData = { files: [file], title: file.name };
  if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function' || !navigator.canShare({ files: [file] })) throw new Error('native_share_unavailable');
  await navigator.share(shareData);
}

async function runImageDownload(blob: Blob, name: string, options: DownloadOptions): Promise<DownloadResult> {
  const started = performance.now();
  const timing = (stage: DownloadTimingStage) => options.onTiming?.(stage, Math.max(0, performance.now() - started));
  const platform = options.platform || 'web';
  const kind = platformKind(platform, options.userAgent ?? navigator.userAgent);
  const file = new File([blob], name, { type: imageFileType(blob, name), lastModified: Date.now() });
  timing('blob_ready');
  options.onEvent?.('download_started', { platform, strategy: 'select' });
  let strategy: DownloadStrategy = kind === 'desktop' ? 'browser_download' : 'native_share';
  try {
    if (kind === 'android') {
      if (options.nativeDownload && options.nativeUrl) {
        timing('ticket_started');
        const ticket = await options.nativeUrl();
        timing('ticket_ready');
        strategy = 'native_download';
        options.onEvent?.('download_native_started', { platform, strategy });
        timing('native_invoked');
        if (!await options.nativeDownload({ url: ticket.url, file_name: ticket.fileName || name })) throw new DOMException('Download cancelled', 'AbortError');
        timing('handoff_started');
        options.onEvent?.('download_completed_or_handoff', { platform, strategy });
        return { strategy: 'native_download' };
      }
      options.onEvent?.('download_fallback', { platform, strategy: 'native_share', reason: 'native_download_unavailable' });
      timing('native_invoked');
      await shareFile(file);
      timing('handoff_started');
      options.onEvent?.('download_shared', { platform, strategy });
      options.onEvent?.('download_completed_or_handoff', { platform, strategy });
      return { strategy: 'native_share' };
    }
    if (kind === 'ios') {
      timing('native_invoked');
      await shareFile(file);
      timing('handoff_started');
      options.onEvent?.('download_shared', { platform, strategy });
      options.onEvent?.('download_completed_or_handoff', { platform, strategy });
      return { strategy: 'native_share' };
    }
    browserDownload(file);
    timing('native_invoked');
    timing('handoff_started');
    options.onEvent?.('download_completed_or_handoff', { platform, strategy });
    return { strategy: 'browser_download' };
  } catch (cause) {
    const cancelled = cause instanceof DOMException && cause.name === 'AbortError';
    const errorCode = cancelled ? 'user_cancelled' : cause instanceof Error ? cause.message.slice(0, 80) : 'download_failed';
    options.onEvent?.(cancelled ? 'download_cancelled' : 'download_failed', { platform, strategy, error_code: errorCode });
    throw cause;
  }
}

export function saveImageFile(blob: Blob, name: string, options: DownloadOptions = {}): Promise<DownloadResult> {
  const key = options.operationKey || name;
  const current = inFlightDownloads.get(key);
  if (current) return current;
  const pending = runImageDownload(blob, name, options);
  inFlightDownloads.set(key, pending);
  const clear = () => { if (inFlightDownloads.get(key) === pending) inFlightDownloads.delete(key); };
  void pending.then(clear, clear);
  return pending;
}
